import type { LevelIdentity, RewardConfig, RewardSnapshot } from '@contracts';

/**
 * Pure PRESENTATION derivation over an already-computed `RewardSnapshot` +
 * `RewardConfig` — this is display math (which name/icon to show, what
 * fraction of the pulse-line to fill), never reward math itself (XP totals,
 * level number, and the level/next-level XP span are all computed by the
 * domain layer per RewardSnapshot's own contract; nothing here recomputes
 * XP or level thresholds). Matches the dev-ui boundary rule ("no business
 * logic in components... if you're tempted to compute, the domain API is
 * missing something") — this only reshapes numbers the domain already
 * returned into what the ambient/transparency UI needs to render, the same
 * category of work as `computePercent` in TodayView.tsx or
 * `formatRelativeDatePill` in format/date.ts.
 *
 * Level-identity lookup handles two edge cases explicitly (RewardConfig's
 * own doc comments): `levelIdentities` may be shorter than
 * `level - 1` for levels beyond the ratified arc — in that case, and beyond
 * the array's real length generally, the LAST identity entry is reused
 * (mirrors `LevelCurve`'s own "reuse the last increment" rule and
 * `DEFAULT_REWARD_CONFIG.levelIdentities`'s explicit level-11 comment).
 */
export interface LevelIdentityInfo {
  readonly identity: LevelIdentity;
  /** Fraction (0-1) of the way through the current level, for the pulse-line fill. */
  readonly fractionToNext: number;
}

export function resolveLevelIdentity(
  snapshot: RewardSnapshot,
  config: RewardConfig,
): LevelIdentityInfo {
  const identities = config.levelIdentities;
  const byLevel = identities.find((i) => i.level === snapshot.level);
  const fallback = identities[identities.length - 1];
  const identity = byLevel ?? fallback ?? { level: snapshot.level, name: `Level ${snapshot.level}`, iconId: 'star' };

  const fractionToNext =
    snapshot.xpRequiredForNextLevel > 0
      ? clamp01(snapshot.xpIntoCurrentLevel / snapshot.xpRequiredForNextLevel)
      : 0;

  return { identity, fractionToNext };
}

/** The next level's identity, for "you are here ... on your way to <next>"-style copy. Null past the curve's last defined identity when there truly is no further name (never happens in practice since the arc reuses its last entry, but kept total for callers that want to special-case "at the top"). */
export function resolveNextLevelIdentity(
  snapshot: RewardSnapshot,
  config: RewardConfig,
): LevelIdentity | null {
  const identities = config.levelIdentities;
  const next = identities.find((i) => i.level === snapshot.level + 1);
  return next ?? identities[identities.length - 1] ?? null;
}

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(1, n));
}
